// Gate A2.1, input on every device (engine/docs/spikes/002-green-valley/brief.md). The mechanisms are @rpg/runtime's
// (the contract and its check, contexts, the buffer, hold or toggle, device tracking, the rebinding model, the touch
// layer); this file passes the spike's data in and wires them to the page:
//   - the contract from content/tables (actions, bindings, contexts), checked at boot: an error is logged, so the
//     harness's no-page-errors invariant fails any spec that boots a broken contract;
//   - the touch layout (content/tables/touch.json) checked against the contract and laid out on screen;
//   - binding overrides and hold modes in localStorage (key `INPUT_PREFS_KEY`), defaults from the settings table;
//   - the buffer's lifetime from tuning (input.bufferWindow), its actions from the actions' `buffer` rank, frozen in
//     hit-stop;
//   - contexts: exploring always, menu over it while the pause or game-over screen is up; pause on the pause action;
//   - the touch overlay in play when touch is in use (or `?touch=1`; `?touch=0` never; `?touch=all` also reveals
//     the buttons the game doesn't use yet), and a small prompt bar following the device used last.
// Planted defects (`?faults=` or `__rpg.faults`): `missing-touch-binding` drops dodge's touch binding (the contract
// check must fail); `buffer-ages-in-hit-stop` lets the buffer age during hit-stop (the buffer check must fail).
import {
  HIT_STOP_SCALE,
  bindActions,
  browserPads,
  checkContract,
  checkTouchContract,
  checkTouchLayout,
  createInput,
  formatIssue,
  layoutTouch,
  type ActionsState,
  type BindingDecl,
  type ContractCheck,
  type DeviceFamily,
  type HoldMode,
  type Input,
  type InputContract,
  type Runtime,
  type SetResult,
  type StorageLike,
  type TouchButtonSpec,
  type TouchLayoutSpec,
  type Vec2,
} from "@rpg/runtime";
import { attachDom, createTouchOverlay, type TouchOverlay, type TouchOverlayState } from "@rpg/runtime/dom";
import type { ContentRegistry } from "@rpg/content";
import type { ContentTables } from "../content";
import type { FaultGroup } from "../faults";
import { createPrompt, type Prompt } from "./prompt";

/** The planted defects this module owns, by their `?faults=` names. */
export const INPUT_FAULT_NAMES = ["missing-touch-binding", "buffer-ages-in-hit-stop"] as const;
/** The binding the missing-touch defect drops. */
export const MISSING_TOUCH_BINDING = "dodge.touch";
/** Where binding overrides and hold modes are stored. */
export const INPUT_PREFS_KEY = "green-valley.input";
/** The actions the prompt bar shows in play. */
export const PROMPT_ACTIONS = ["move", "look", "jump", "sprint", "dodge"] as const;
/** The contexts the touch overlay serves come from the contexts table (`touch: true`). */

/** Everything the game reads from its content to build its input. */
export interface InputData {
  contract: InputContract;
  layout: TouchLayoutSpec;
  /** The touch buttons' text (string ids). */
  buttonLabels: Record<string, string>;
  /** family → glyph key → string id. */
  glyphs: Partial<Record<DeviceFamily, Record<string, string>>>;
  /** Default hold modes from the settings table (`controls.<action>`). */
  holdModes: Record<string, HoldMode>;
  /** The buffered actions, highest priority first. */
  buffered: string[];
  bufferSeconds: number;
  /** The contexts the touch overlay serves. */
  touchContexts: string[];
}

/** Build the input data from the content. */
export function inputFromContent(reg: ContentRegistry<ContentTables>): InputData {
  const contract: InputContract = {
    contexts: reg.rows("contexts").map((c) => ({ id: c.id, priority: c.priority, blocks: c.blocks, base: c.base })),
    actions: reg.rows("actions").map((a) => ({ id: a.id, type: a.kind, context: a.context.id, holdOrToggle: a.holdOrToggle, waive: a.waive, shares: a.shares?.id })),
    bindings: reg
      .rows("bindings")
      .map((b): BindingDecl => ({ id: b.id, action: b.action.id, device: b.device, shape: b.shape, controls: b.controls, scale: b.scale as [number, number] | undefined })),
  };
  const rows = reg.rows("touch");
  const stick = rows.find((r) => r.kind === "stick");
  const look = rows.find((r) => r.kind === "look");
  const arc = rows.find((r) => r.kind === "arc");
  if (!stick?.control || !look?.control) throw new Error("the touch table needs a stick and a look row with controls");
  const buttons = rows.filter((r) => r.kind === "button");
  const layout: TouchLayoutSpec = {
    stick: { control: stick.control, run: stick.run, zone: { right: stick.zone?.right ?? 0.45, top: stick.zone?.top ?? 0.28 } },
    look: { control: look.control, zone: { left: look.zone?.left ?? 0.5 } },
    arc: arc ? { from: arc.from ?? 14, to: arc.to ?? 76 } : undefined,
    buttons: buttons.map((b): TouchButtonSpec => ({ id: b.id, control: b.control ?? `Touch:${b.id}`, size: b.size ?? "secondary", place: b.place as TouchButtonSpec["place"], hidden: b.hidden })),
  };
  const glyphs: InputData["glyphs"] = {};
  for (const g of reg.rows("glyphs")) (glyphs[g.family] ??= {})[g.id] = g.label.id;
  const holdModes: Record<string, HoldMode> = {};
  for (const a of contract.actions) {
    const s = a.holdOrToggle ? reg.get("settings", `controls.${a.id}`) : undefined;
    if (s && (s.default === "hold" || s.default === "toggle")) holdModes[a.id] = s.default;
  }
  return {
    contract,
    layout,
    buttonLabels: Object.fromEntries(buttons.filter((b) => b.label).map((b) => [b.id, b.label!.id])),
    glyphs,
    holdModes,
    buffered: reg
      .rows("actions")
      .filter((a) => a.buffer !== undefined)
      .sort((a, b) => a.buffer! - b.buffer!)
      .map((a) => a.id),
    bufferSeconds: reg.get("tuning", "input.bufferWindow")?.value ?? 0.22,
    touchContexts: reg.rows("contexts").filter((c) => c.touch).map((c) => c.id),
  };
}

/** localStorage, or null where it isn't allowed (a private window, a sandbox). */
function pageStorage(): StorageLike | null {
  try {
    const s = window.localStorage;
    s.getItem(INPUT_PREFS_KEY);
    return s;
  } catch {
    return null;
  }
}

/** What the input module needs from the rest of the spike. */
export interface InputDeps {
  runtime: Runtime;
  registry: ContentRegistry<ContentTables>;
  t: (id: string) => string;
  /** The game flow (the boot shell's): its state, and pause and resume. */
  flow: { state(): string | null; pause(): void; resume(): void };
  /** The page's switches (default `location.search`). */
  search?: string;
  /** Whether a UI screen is open over the game (src/ui: `ui.top() !== null`); the menu context is on meanwhile. */
  menuOpen?: () => boolean;
}

/** The contract report on the handle. */
export interface InputReport {
  ok: boolean;
  issues: ContractCheck["issues"];
  families: ContractCheck["families"];
  /** The touch layout against the contract (every touch binding written by the layout, every layout control bound). */
  touch: string[];
  /** The touch layout at the current viewport (overlaps, safe areas, sizes); empty in portrait, which is not checked. */
  layout: string[];
  /** Stored prefs that couldn't be applied. */
  prefs: string[];
}

export interface SpikeInput {
  input: Input;
  overlay: TouchOverlay;
  prompt: Prompt;
  /** The planted-defect switches, for `__rpg.faults`. */
  faults: FaultGroup;
  /** A summary for `state()` (finite, JSON-safe). */
  state(): { device: DeviceFamily | null; contexts: string[]; touch: boolean; buffered: string | null };
  /** Keys for `window.__rpg.input`. */
  handle: ReturnType<typeof makeHandle>;
}

/** Build the input from the content and wire it to the page and the runtime. */
export function setupInput(d: InputDeps): SpikeInput {
  const q = new URLSearchParams(d.search ?? location.search);
  const planted = new Set((q.get("faults") ?? "").split(",").filter(Boolean));
  const data = inputFromContent(d.registry);
  const { runtime } = d;

  const input = createInput({
    contract: data.contract,
    now: () => runtime.time,
    dt: runtime.clock.dt,
    storage: pageStorage(),
    storageKey: INPUT_PREFS_KEY,
    holdModes: data.holdModes,
    buffer: { priority: data.buffered, lifetime: data.bufferSeconds, frozen: () => runtime.clock.hitStopRemaining > 0 },
  });
  bindActions(runtime, input.actions, { pads: browserPads });
  const buffer = input.actions.buffer!;
  buffer.faults.agesInHitStop = planted.has("buffer-ages-in-hit-stop");

  // the contract in force: the rebinding model's, minus dodge's touch binding while that planted defect is on (it
  // applies live: the actions lose the binding too)
  const current = (): InputContract => {
    const eff = input.rebinding.effective();
    return planted.has("missing-touch-binding") ? { ...eff, bindings: eff.bindings.filter((b) => b.id !== MISSING_TOUCH_BINDING) } : eff;
  };
  const applyContract = (): void => input.contexts.setContract(current());
  input.rebinding.onChange(applyContract);
  applyContract();

  // the checks, at boot: errors are logged (the harness's no-page-errors invariant sees them)
  const report = (): InputReport => {
    const c = checkContract(current());
    // the layer is landscape (decision 0032): a portrait viewport is not checked (the boot asks for landscape on phones)
    const l = innerHeight > innerWidth ? [] : checkTouchLayout(layoutTouch(data.layout, innerWidth, innerHeight)).map((i) => i.message);
    const touch = checkTouchContract(current(), data.layout, data.touchContexts);
    return { ok: c.ok && l.length === 0 && touch.length === 0, issues: c.issues, families: c.families, touch, layout: l, prefs: [...input.rebinding.loadIssues] };
  };
  const logReport = (r: InputReport): void => {
    for (const i of r.issues) if (i.severity === "error") console.error(formatIssue(i));
    for (const m of [...r.touch, ...r.layout]) console.error(`[input touch] ${m}`);
    for (const m of r.prefs) console.warn(`[input prefs] ${m}`);
  };
  logReport(report());

  // devices: keys and the mouse from the page (touch pointers go to the overlay); pads polled each frame
  attachDom(input.controls, window, document.body, { touchPointers: "ignore" });
  const overlay = createTouchOverlay({
    controls: input.controls,
    spec: data.layout,
    label: (b) => (data.buttonLabels[b.id] ? d.t(data.buttonLabels[b.id]!) : ""),
  });
  const touchSwitch = q.get("touch");
  if (touchSwitch === "all") for (const b of data.layout.buttons) overlay.reveal(b.id, true);
  const coarse = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  const label = (action: string): string => {
    const row = d.registry.get("actions", action);
    return row ? d.t(row.label.id) : action;
  };
  const prompt = createPrompt({ input, glyphs: data.glyphs, t: (id) => d.t(id), label, actions: PROMPT_ACTIONS });

  // frame-level actions: pause from play; back resumes
  input.contexts.on("pause", () => d.flow.state() === "play" && d.flow.pause());
  // back resumes from the pause screen, unless a UI owns the screens (src/ui: its pause screen takes Escape as back)
  input.contexts.on("back", () => !d.menuOpen && d.flow.state() === "pause" && d.flow.resume());

  // each frame (frames run while paused too): the menu context follows the flow's screens; the overlay and prompt
  // show in play, the overlay only when touch is in use and every active context is one it serves
  const sync = (): void => {
    const flow = d.flow.state();
    // a screen open over the game in play (the UI's stack: a sync conflict, a menu) mutes gameplay actions too; at
    // the title and while loading the flow keeps the sim paused, and the harness drives it there on purpose
    input.contexts.set("menu", flow === "pause" || flow === "gameover" || (flow === "play" && (d.menuOpen?.() ?? false)));
    const playing = flow === "play";
    const touchOn = touchSwitch === "0" ? false : touchSwitch === "1" || touchSwitch === "all" || input.controls.device === "touch" || (coarse && input.controls.device === null);
    overlay.show(playing && touchOn && input.contexts.active().every((c) => data.touchContexts.includes(c)));
    prompt.show(playing);
  };
  runtime.on("input", sync, { name: "input: contexts and overlay", order: -2e9 });
  input.controls.onDevice(sync);
  sync();

  // the buffer check's consumer (harness only, idle unless armed): a state that becomes legal `after` seconds of sim
  // time from now takes the buffered press at its first step, the way a move's chain point would
  const probe: Probe = { armed: null };
  runtime.on(
    "sim",
    (c) => {
      const p = probe.armed;
      if (p && !p.result && c.time >= p.at - 1e-9) p.result = { step: c.step, got: buffer.consume(p.accept) };
    },
    { name: "input: buffer probe", order: -1e8 },
  );

  const faults: FaultGroup = {
    names: INPUT_FAULT_NAMES,
    get: (n) => (n === "buffer-ages-in-hit-stop" ? buffer.faults.agesInHitStop : planted.has(n)),
    set: (n, on) => {
      if (on) planted.add(n);
      else planted.delete(n);
      if (n === "buffer-ages-in-hit-stop") buffer.faults.agesInHitStop = on;
      else {
        applyContract();
        logReport(report());
      }
    },
  };

  return {
    input,
    overlay,
    prompt,
    faults,
    state: () => ({ device: input.controls.device, contexts: input.contexts.active(), touch: overlay.shown, buffered: buffer.action }),
    handle: makeHandle(input, overlay, prompt, () => report(), runtime, probe),
  };
}

/** The buffer check's armed consumer. */
interface Probe {
  armed: { accept: string[] | undefined; at: number; result?: { step: number; got: string | null } } | null;
}

function makeHandle(input: Input, overlay: TouchOverlay, prompt: Prompt, report: () => InputReport, runtime: Runtime, probe: Probe) {
  const { actions, contexts, rebinding } = input;
  return {
    /** The contract check, the touch cross-checks and the stored-prefs issues. */
    contract: report,
    /** Every action's state, the buffer, the device, the contexts and the hold modes. */
    describe: () => ({
      ...actions.describe(),
      buffer: actions.buffer!.snapshot(),
      device: input.controls.device,
      contexts: contexts.active(),
      holdModes: Object.fromEntries(rebinding.list().filter((a) => a.holdOrToggle).map((a) => [a.id, actions.holdMode(a.id)])),
    }),
    /** The device family used last. */
    device: (): DeviceFamily | null => input.controls.device,
    /** Injected input on sim time (the sim's A1 handle keys, kept): override the move stick, hold a control. */
    stick: (x: number, y: number): void => actions.setStick("move", { x, y }),
    clearStick: (): void => actions.setStick("move", null),
    hold: (control: string, seconds: number): void => input.controls.hold(control, seconds),
    holdAction: (action: string, seconds: number): void => actions.holdAction(action, seconds),
    tapAction: (action: string): void => actions.tapAction(action),
    /** The controller stream's name for `tapAction` (its specs use it). */
    tap: (action: string): void => actions.tapAction(action),
    /** The buffered press now, and consume it if `accept` allows (a state taking it at its first legal moment). */
    buffered: () => actions.buffer!.snapshot(),
    consume: (accept?: string | string[]): string | null => actions.buffer!.consume(accept),
    /** Start a hit-stop of `seconds` real time (combat owns this from phase B; here for the buffer check). A scaled
     *  hit-stop (0.06), so slowed steps run through it and the buffer's freeze is what keeps the press: with the split
     *  hit-stop (phase B) no step runs in a plain one at all. */
    hitStop: (seconds: number): void => runtime.clock.hitStop(seconds, HIT_STOP_SCALE),
    contexts: { active: () => contexts.active(), push: (id: string) => contexts.push(id), pop: (id: string) => contexts.pop(id) },
    /** Hold or toggle for sprint and block (decision 0063); stored with the bindings. */
    setHoldMode: (action: string, mode: HoldMode): SetResult => rebinding.setHoldMode(action, mode),
    holdMode: (action: string): HoldMode => actions.holdMode(action),
    /** The rebinding model (the A4 rebinding screen's API; see @rpg/runtime's rebind.ts). */
    rebinding: {
      list: () => rebinding.list(),
      set: (id: string, controls: string[], conflicts?: "reject" | "allow" | "swap") => rebinding.set(id, controls, { conflicts }),
      setPart: (id: string, part: number, control: string, conflicts?: "reject" | "allow" | "swap") => rebinding.setPart(id, part, control, { conflicts }),
      reset: (id?: string) => rebinding.reset(id),
      conflicts: () => rebinding.conflicts(),
      prefs: () => rebinding.prefs(),
      startCapture: (id: string, part?: number) => void rebinding.startCapture(id, input.controls, { part }),
      capturing: () => rebinding.capturing(),
    },
    /** The touch overlay. */
    touch: {
      state: (): TouchOverlayState => overlay.state(),
      reveal: (id: string, on: boolean) => overlay.reveal(id, on),
    },
    /** The prompt bar. */
    prompt: () => prompt.state(),
    /** Arm the buffer check's consumer: at the first sim step `afterSeconds` from now it consumes the buffered press
     *  if it is one of `accept`; `probeResult()` then says which step and what it got. */
    armConsume: (accept: string | string[] | undefined, afterSeconds: number): void => {
      probe.armed = { accept: accept === undefined ? undefined : typeof accept === "string" ? [accept] : accept, at: runtime.time + afterSeconds };
    },
    probeResult: () => probe.armed?.result ?? null,
    /** Take the look delta accumulated since the last take (what a camera would read this frame). */
    takeLook: (): Vec2 => actions.consumeDelta("look", 0),
    /** Read the actions as a sim step would (for checks between steps): value, stick. */
    read: (action: string): { value: number; stick: Vec2 } => ({ value: actions.value(action), stick: actions.stick(action) }),
    /** The input's part of a save (injected holds, latched presses, toggles, the buffer). */
    snapshot: (): ActionsState => actions.snapshot(),
  };
}
